import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_ENDPOINTS } from "../config/constants";
import api from "../utils/api";
import { getReadableApiError } from "../utils/readableApiResponse";
import { logout } from "./authSlice";

const GOOGLE_AUTHORIZATION_FAILED_MESSAGE =
    "Google authorization failed. Please reconnect this sender.";
const CONNECTION_TEST_FAILED_MESSAGE =
    "Connection test failed. Please try again.";

const getInitialConnectionsState = () => ({
    connections: [],
    connectionsStatus: "idle",
    connectionsError: null,

    googleOAuthStart: null,
    googleOAuthStartStatus: "idle",
    googleOAuthStartError: null,

    createSmtpConnectionStatus: "idle",
    createSmtpConnectionError: null,

    setPrimaryStatus: "idle",
    setPrimaryError: null,

    updatePriorityStatusByConnectionId: {},
    updatePriorityErrorByConnectionId: {},

    deleteStatusByConnectionId: {},
    deleteErrorByConnectionId: {},

    testResultsByConnectionId: {},
    testStatusByConnectionId: {},
    testErrorByConnectionId: {},
});

const getTrimmedString = (value) =>
    typeof value === "string" || typeof value === "number"
        ? String(value).trim()
        : "";

const getConnectionId = (value) => {
    if (typeof value === "string" || typeof value === "number") {
        return String(value).trim();
    }

    return (
        getTrimmedString(value?.connectionId) ||
        getTrimmedString(value?.connection_id) ||
        getTrimmedString(value?.id)
    );
};

const getUserId = (value) => {
    if (typeof value === "string" || typeof value === "number") {
        return String(value).trim();
    }

    return getTrimmedString(value?.userId) || getTrimmedString(value?.user_id);
};

const getSafeConnections = (value) => (Array.isArray(value) ? value : []);

const getConnectionApiError = (error, fallbackMessage) => {
    const detail = error.response?.data?.detail;

    if (typeof detail?.message === "string" && detail.message.trim()) {
        return detail.message.trim();
    }

    return getReadableApiError(error, fallbackMessage);
};

const getConnectionTestApiError = (error) => {
    const detail = error.response?.data?.detail;
    const errorReason = getTrimmedString(detail?.error_reason).toLowerCase();
    const provider = getTrimmedString(detail?.provider).toLowerCase();
    const message = getTrimmedString(detail?.message || error.message);
    const normalizedMessage = message.toLowerCase();
    const isGoogleOAuthProvider = provider.includes("google");
    const isGoogleApiMessage = normalizedMessage.includes("googleapis");
    const isGoogleOAuthUnauthorized =
        normalizedMessage.includes("401") ||
        normalizedMessage.includes("unauthorized");

    if (
        (errorReason === "connection_health_check_failed" &&
            isGoogleOAuthProvider) ||
        ((isGoogleOAuthProvider || isGoogleApiMessage) &&
            isGoogleOAuthUnauthorized)
    ) {
        return GOOGLE_AUTHORIZATION_FAILED_MESSAGE;
    }

    return CONNECTION_TEST_FAILED_MESSAGE;
};

const upsertConnection = (connections, connection, fallbackConnectionId) => {
    const connectionId = getConnectionId(connection) || fallbackConnectionId;

    if (!connectionId) return connections;

    const existingIndex = connections.findIndex(
        (item) => getConnectionId(item) === connectionId,
    );

    if (existingIndex < 0) {
        return [...connections, { ...connection, id: connectionId }];
    }

    return connections.map((item, index) =>
        index === existingIndex ? { ...item, ...connection } : item,
    );
};

export const fetchConnections = createAsyncThunk(
    "connections/fetchConnections",
    async (_, thunkAPI) => {
        try {
            const response = await api.get(API_ENDPOINTS.CONNECTIONS);

            return getSafeConnections(response.data);
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getConnectionApiError(error, "Failed to fetch connections."),
            );
        }
    },
);

export const startGoogleOAuth = createAsyncThunk(
    "connections/startGoogleOAuth",
    async (userId, thunkAPI) => {
        const stateUserId = getTrimmedString(
            thunkAPI.getState()?.auth?.user?.id,
        );
        const normalizedUserId = getUserId(userId) || stateUserId;

        if (!normalizedUserId) {
            return thunkAPI.rejectWithValue(
                "User ID is required to start Google OAuth.",
            );
        }

        try {
            const response = await api.get(API_ENDPOINTS.GOOGLE_OAUTH_START, {
                params: {
                    user_id: normalizedUserId,
                },
            });

            return response.data || null;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getConnectionApiError(
                    error,
                    "Failed to start Google connection.",
                ),
            );
        }
    },
);

export const createSmtpConnection = createAsyncThunk(
    "connections/createSmtpConnection",
    async (smtpPayload = {}, thunkAPI) => {
        try {
            const response = await api.post(
                API_ENDPOINTS.CREATE_SMTP_CONNECTION,
                smtpPayload,
            );

            return response.data || null;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getConnectionApiError(
                    error,
                    "Failed to create SMTP connection.",
                ),
            );
        }
    },
);

export const setPrimaryConnection = createAsyncThunk(
    "connections/setPrimaryConnection",
    async (connectionId, thunkAPI) => {
        const normalizedConnectionId = getConnectionId(connectionId);

        if (!normalizedConnectionId) {
            return thunkAPI.rejectWithValue("Connection ID is required.");
        }

        try {
            const response = await api.post(
                API_ENDPOINTS.CONNECTION_SET_PRIMARY(normalizedConnectionId),
            );

            return {
                connectionId: normalizedConnectionId,
                connection: response.data || null,
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getConnectionApiError(
                    error,
                    "Failed to set primary connection.",
                ),
            );
        }
    },
);

export const updateConnectionPriority = createAsyncThunk(
    "connections/updateConnectionPriority",
    async ({ connectionId, priority } = {}, thunkAPI) => {
        const normalizedConnectionId = getConnectionId(connectionId);
        const numericPriority = Number(priority);

        if (!normalizedConnectionId) {
            return thunkAPI.rejectWithValue("Connection ID is required.");
        }

        if (!Number.isFinite(numericPriority)) {
            return thunkAPI.rejectWithValue(
                "Connection priority must be a finite number.",
            );
        }

        try {
            const response = await api.patch(
                API_ENDPOINTS.CONNECTION_BY_ID(normalizedConnectionId),
                {
                    priority: numericPriority,
                },
            );

            return {
                connectionId: normalizedConnectionId,
                priority: numericPriority,
                connection: response.data || null,
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getConnectionApiError(
                    error,
                    "Failed to update connection priority.",
                ),
            );
        }
    },
);

export const deleteConnection = createAsyncThunk(
    "connections/deleteConnection",
    async (connectionId, thunkAPI) => {
        const normalizedConnectionId = getConnectionId(connectionId);

        if (!normalizedConnectionId) {
            return thunkAPI.rejectWithValue("Connection ID is required.");
        }

        try {
            const response = await api.delete(
                API_ENDPOINTS.CONNECTION_BY_ID(normalizedConnectionId),
            );

            return response.data || null;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getConnectionApiError(error, "Failed to delete connection."),
            );
        }
    },
);

export const testConnection = createAsyncThunk(
    "connections/testConnection",
    async (connectionId, thunkAPI) => {
        const normalizedConnectionId = getConnectionId(connectionId);

        if (!normalizedConnectionId) {
            return thunkAPI.rejectWithValue("Connection ID is required.");
        }

        try {
            const response = await api.post(
                API_ENDPOINTS.CONNECTION_TEST(normalizedConnectionId),
            );

            return {
                connectionId: normalizedConnectionId,
                result: response.data || null,
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(
                getConnectionTestApiError(error),
            );
        }
    },
);

const connectionsSlice = createSlice({
    name: "connections",
    initialState: getInitialConnectionsState(),
    reducers: {
        clearGoogleOAuthStartState: (state) => {
            state.googleOAuthStart = null;
            state.googleOAuthStartStatus = "idle";
            state.googleOAuthStartError = null;
        },
        clearSetPrimaryConnectionState: (state) => {
            state.setPrimaryStatus = "idle";
            state.setPrimaryError = null;
        },
        clearCreateSmtpConnectionState: (state) => {
            state.createSmtpConnectionStatus = "idle";
            state.createSmtpConnectionError = null;
        },
        clearUpdateConnectionPriorityState: (state, action) => {
            const connectionId = getConnectionId(action.payload);

            if (!connectionId) {
                state.updatePriorityStatusByConnectionId = {};
                state.updatePriorityErrorByConnectionId = {};
                return;
            }

            delete state.updatePriorityStatusByConnectionId[connectionId];
            delete state.updatePriorityErrorByConnectionId[connectionId];
        },
        clearDeleteConnectionState: (state, action) => {
            const connectionId = getConnectionId(action.payload);

            if (!connectionId) {
                state.deleteStatusByConnectionId = {};
                state.deleteErrorByConnectionId = {};
                return;
            }

            delete state.deleteStatusByConnectionId[connectionId];
            delete state.deleteErrorByConnectionId[connectionId];
        },
        clearTestConnectionState: (state, action) => {
            const connectionId = getConnectionId(action.payload);

            if (!connectionId) {
                state.testResultsByConnectionId = {};
                state.testStatusByConnectionId = {};
                state.testErrorByConnectionId = {};
                return;
            }

            delete state.testResultsByConnectionId[connectionId];
            delete state.testStatusByConnectionId[connectionId];
            delete state.testErrorByConnectionId[connectionId];
        },
        clearConnectionsError: (state) => {
            state.connectionsError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchConnections.pending, (state) => {
                state.connectionsStatus = "loading";
                state.connectionsError = null;
            })
            .addCase(fetchConnections.fulfilled, (state, action) => {
                state.connectionsStatus = "succeeded";
                state.connectionsError = null;
                state.connections = action.payload;
            })
            .addCase(fetchConnections.rejected, (state, action) => {
                state.connectionsStatus = "failed";
                state.connectionsError = action.payload;
            })
            .addCase(startGoogleOAuth.pending, (state) => {
                state.googleOAuthStartStatus = "loading";
                state.googleOAuthStartError = null;
                state.googleOAuthStart = null;
            })
            .addCase(startGoogleOAuth.fulfilled, (state, action) => {
                state.googleOAuthStartStatus = "succeeded";
                state.googleOAuthStartError = null;
                state.googleOAuthStart = action.payload;
            })
            .addCase(startGoogleOAuth.rejected, (state, action) => {
                state.googleOAuthStartStatus = "failed";
                state.googleOAuthStartError = action.payload;
            })
            .addCase(createSmtpConnection.pending, (state) => {
                state.createSmtpConnectionStatus = "loading";
                state.createSmtpConnectionError = null;
            })
            .addCase(createSmtpConnection.fulfilled, (state, action) => {
                state.createSmtpConnectionStatus = "succeeded";
                state.createSmtpConnectionError = null;

                if (action.payload) {
                    state.connections = upsertConnection(
                        state.connections,
                        action.payload,
                    );
                }
            })
            .addCase(createSmtpConnection.rejected, (state, action) => {
                state.createSmtpConnectionStatus = "failed";
                state.createSmtpConnectionError = action.payload;
            })
            .addCase(setPrimaryConnection.pending, (state) => {
                state.setPrimaryStatus = "loading";
                state.setPrimaryError = null;
            })
            .addCase(setPrimaryConnection.fulfilled, (state, action) => {
                const { connectionId, connection } = action.payload;
                const updatedConnection = {
                    ...(connection || {}),
                    id: connectionId,
                    is_primary: true,
                };

                state.setPrimaryStatus = "succeeded";
                state.setPrimaryError = null;
                state.connections = upsertConnection(
                    state.connections.map((item) => ({
                        ...item,
                        is_primary: getConnectionId(item) === connectionId,
                    })),
                    updatedConnection,
                    connectionId,
                );
            })
            .addCase(setPrimaryConnection.rejected, (state, action) => {
                state.setPrimaryStatus = "failed";
                state.setPrimaryError = action.payload;
            })
            .addCase(updateConnectionPriority.pending, (state, action) => {
                const connectionId = getConnectionId(action.meta.arg);

                if (!connectionId) return;

                state.updatePriorityStatusByConnectionId[connectionId] =
                    "loading";
                state.updatePriorityErrorByConnectionId[connectionId] = null;
            })
            .addCase(updateConnectionPriority.fulfilled, (state, action) => {
                const { connectionId, connection, priority } = action.payload;

                state.updatePriorityStatusByConnectionId[connectionId] =
                    "succeeded";
                state.updatePriorityErrorByConnectionId[connectionId] = null;

                if (connection) {
                    state.connections = upsertConnection(
                        state.connections,
                        connection,
                        connectionId,
                    );
                    return;
                }

                state.connections = state.connections.map((item) =>
                    getConnectionId(item) === connectionId
                        ? { ...item, priority }
                        : item,
                );
            })
            .addCase(updateConnectionPriority.rejected, (state, action) => {
                const connectionId = getConnectionId(action.meta.arg);

                if (!connectionId) return;

                state.updatePriorityStatusByConnectionId[connectionId] =
                    "failed";
                state.updatePriorityErrorByConnectionId[connectionId] =
                    action.payload;
            })
            .addCase(deleteConnection.pending, (state, action) => {
                const connectionId = getConnectionId(action.meta.arg);

                if (!connectionId) return;

                state.deleteStatusByConnectionId[connectionId] = "loading";
                state.deleteErrorByConnectionId[connectionId] = null;
            })
            .addCase(deleteConnection.fulfilled, (state, action) => {
                const connectionId =
                    getConnectionId(action.payload) ||
                    getConnectionId(action.meta.arg);

                if (!connectionId) return;

                state.deleteStatusByConnectionId[connectionId] = "succeeded";
                state.deleteErrorByConnectionId[connectionId] = null;
                state.connections = state.connections.filter(
                    (connection) => getConnectionId(connection) !== connectionId,
                );
                delete state.updatePriorityStatusByConnectionId[connectionId];
                delete state.updatePriorityErrorByConnectionId[connectionId];
                delete state.testResultsByConnectionId[connectionId];
                delete state.testStatusByConnectionId[connectionId];
                delete state.testErrorByConnectionId[connectionId];
            })
            .addCase(deleteConnection.rejected, (state, action) => {
                const connectionId = getConnectionId(action.meta.arg);

                if (!connectionId) return;

                state.deleteStatusByConnectionId[connectionId] = "failed";
                state.deleteErrorByConnectionId[connectionId] = action.payload;
            })
            .addCase(testConnection.pending, (state, action) => {
                const connectionId = getConnectionId(action.meta.arg);

                if (!connectionId) return;

                state.testStatusByConnectionId[connectionId] = "loading";
                state.testErrorByConnectionId[connectionId] = null;
                delete state.testResultsByConnectionId[connectionId];
            })
            .addCase(testConnection.fulfilled, (state, action) => {
                const { connectionId, result } = action.payload;
                const storedResult =
                    result && typeof result === "object" ? result : {};

                state.testResultsByConnectionId[connectionId] = {
                    ...storedResult,
                    tested_at: new Date().toISOString(),
                };
                state.testStatusByConnectionId[connectionId] = "succeeded";
                state.testErrorByConnectionId[connectionId] = null;
            })
            .addCase(testConnection.rejected, (state, action) => {
                const connectionId = getConnectionId(action.meta.arg);

                if (!connectionId) return;

                state.testStatusByConnectionId[connectionId] = "failed";
                state.testErrorByConnectionId[connectionId] = action.payload;
            })
            .addCase(logout, () => getInitialConnectionsState());
    },
});

export const {
    clearGoogleOAuthStartState,
    clearSetPrimaryConnectionState,
    clearCreateSmtpConnectionState,
    clearUpdateConnectionPriorityState,
    clearDeleteConnectionState,
    clearTestConnectionState,
    clearConnectionsError,
} = connectionsSlice.actions;

export const selectConnectionsState = (state) => state.connections;
export const selectConnections = (state) =>
    state.connections?.connections || [];
export const selectActiveConnections = (state) =>
    selectConnections(state).filter((connection) => connection?.is_active);
export const selectPrimaryConnection = (state) =>
    selectActiveConnections(state).find(
        (connection) => connection?.is_primary,
    ) || null;
export const selectHasActiveConnection = (state) =>
    selectActiveConnections(state).length > 0;

export default connectionsSlice.reducer;
